'use client';

import { clsx } from 'clsx';
import { omit } from 'lodash';
import { useRouter } from 'next/navigation';
import { ChangeEvent, FC, InputHTMLAttributes } from 'react';
import { FieldWrapper } from 'src/general/components/forms/Input/atoms/FieldWrapper';
import { getInputAria } from 'src/general/components/forms/Input/Input.helpers';
import { InputBase } from 'src/general/components/forms/Input/Input.type';
import { FilterValues } from 'src/types/CataloguePage.types';
import { FilterOption, PlpFiltersConditionalProps } from '../../PlpFilters.type';

type FilterSelectOption<InputValue> = InputBase<InputValue> & {
  options: FilterOption[];
};

type Props = Prettify<
  FilterSelectOption<string | number> &
    Omit<InputHTMLAttributes<HTMLSelectElement>, 'size'> &
    PlpFiltersConditionalProps
>;

export const FilterSelectItem: FC<Props> = ({ className, placeholder, labelOutside, subLabel, options, ...props }) => {
  const router = useRouter();
  const selectProps = omit(props, 'options', 'size', 'value', 'onSelectFilter', 'onSelectHierarchy', 'data-testid');
  const testId = (props as any)['data-testid'] || 'filter-select';

  const onSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    if (props.type === 'sidebar') {
      const href = e.target.options[e.target.selectedIndex].dataset.href;
      if (href) {
        router.push(href);
      }
    } else {
      props.onSelectFilter({
        slug: e.target.options[e.target.selectedIndex].dataset.slug ?? '',
        value: e.target.value,
        filter: e.target.options[e.target.selectedIndex].dataset.filter as FilterValues,
      });
    }
  };

  return (
    <FieldWrapper
      {...props}
      className={clsx(className, 'selectWrapper')}
      labelOutside={labelOutside}
      subLabel={subLabel}
    >
      <select {...getInputAria(props)} {...selectProps} onChange={onSelect} data-testid={testId}>
        {options.map((option) => (
          <option
            data-href={option.href}
            data-slug={option.slug}
            data-filter={option.filter}
            value={option.value}
            key={option.value}
          >
            {option.label}
          </option>
        ))}
      </select>
    </FieldWrapper>
  );
};
